import { Briefcase, Clock, Handshake, UserPlus, Users } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { formatSydneyDateShort } from '../../../utils/helpers';
import { HrBarList } from '../components/BarList';
import { HrDataTable } from '../components/DataTable';
import type { HrColumn } from '../components/DataTable';
import { HrKpiGrid } from '../components/KpiGrid';
import { HrStatusPill } from '../components/StatusPill';
import {
  PIPELINE_STATUSES,
  countBy,
  daysBetween,
  filterRequisitions,
  formatNumber,
  isOfferAccepted,
  isPipelineCandidate,
  offerAwaitingReply,
  percent,
  requisitionStatusTone,
  salaryRange,
  sortCountsDesc,
} from '../hr-dashboard-utils';
import type { HrRequisition } from '../../../types/hr-dashboard.types';
import type { HrSectionProps } from './types';

const STATUS_LABELS: Record<string, string> = {
  SHORTLISTED: 'Shortlisted',
  PHONE_SCREEN: 'Phone screen',
  INTERVIEW: 'Interview',
  PRE_EMPLOYMENT_COMMENCED: 'Pre-employment started',
  PRE_EMPLOYMENT_COMPLETED: 'Pre-employment complete',
  DRAFT_OFFER: 'Draft offer',
  OFFER_SENT: 'Offer sent',
  OFFER_ACCEPTED: 'Offer accepted',
  HIRING_COMMENCED: 'Hiring commenced',
  HIRED: 'Hired',
  REJECTED: 'Rejected',
  WITHDREW: 'Withdrew',
  WITHDRAWN: 'Withdrawn',
  OFFER_REJECTED: 'Offer rejected',
  OFFER_EXPIRED: 'Offer expired',
};

const REQUISITION_STATUS_LABELS: Record<string, string> = {
  OPEN: 'Open',
  IN_PROGRESS: 'In progress',
  ON_HOLD: 'On hold',
  COMPLETE: 'Complete',
  CANCELLED: 'Cancelled',
};

const TIMELINE_COLOURS: Record<string, string> = {
  COMPLETE: 'var(--chart-3)',
  IN_PROGRESS: 'var(--chart-2)',
  OPEN: 'var(--chart-2)',
  ON_HOLD: 'var(--warning)',
  CANCELLED: 'var(--muted-foreground)',
};

export function RecruitmentSection({ data, filters, asOf, canExport, checkExport }: HrSectionProps) {
  const requisitions = filterRequisitions(data.requisitions, filters);
  const requisitionIds = new Set(requisitions.map((requisition) => requisition.id));
  const candidates = data.candidates.filter((candidate) => requisitionIds.has(candidate.req));

  const openRequisitions = requisitions.filter(
    (requisition) => requisition.status === 'IN_PROGRESS' || requisition.status === 'OPEN'
  );
  const onHold = requisitions.filter((requisition) => requisition.status === 'ON_HOLD');
  const pipeline = candidates.filter(isPipelineCandidate);
  const accepted = candidates.filter(isOfferAccepted);
  const awaiting = candidates.filter(offerAwaitingReply);

  const closed = requisitions.filter((requisition) => requisition.status === 'COMPLETE');
  const closeDurations = closed
    .map((requisition) => daysBetween(requisition.opened, requisition.updated))
    .filter((days): days is number => days !== null);
  const averageClose =
    closeDurations.length > 0
      ? Math.round(closeDurations.reduce((total, days) => total + days, 0) / closeDurations.length)
      : null;

  const offerAcceptance = percent(
    accepted.length,
    accepted.length + awaiting.length
  );

  const coreFunnel = [
    'SHORTLISTED',
    'PHONE_SCREEN',
    'INTERVIEW',
    'OFFER_SENT',
    'OFFER_ACCEPTED',
    'HIRING_COMMENCED',
    'HIRED',
    'REJECTED',
  ];
  const funnelOrder = [
    ...PIPELINE_STATUSES,
    'HIRED',
    'REJECTED',
    'WITHDRAWN',
    'WITHDREW',
    'OFFER_REJECTED',
    'OFFER_EXPIRED',
  ];

  const funnel = funnelOrder
    .map((status) => ({
      key: status,
      label: STATUS_LABELS[status] || status,
      value: candidates.filter((candidate) => candidate.status === status).length,
      color: 'var(--chart-3)',
    }))
    .filter((entry) => entry.value > 0 || coreFunnel.includes(entry.key));

  const sourceItems = sortCountsDesc(countBy(candidates, (candidate) => candidate.source)).map(
    ([label, value]) => ({ key: label, label, value })
  );

  const perRequisition = requisitions
    .map((requisition) => ({
      key: String(requisition.id),
      label: `${requisition.req} ${requisition.role || '—'}`,
      value: candidates.filter((candidate) => candidate.req === requisition.id).length,
    }))
    .sort((a, b) => b.value - a.value);

  const timelineRows = requisitions
    .filter((requisition) => requisition.opened)
    .sort((a, b) => (a.opened || '').localeCompare(b.opened || ''));

  const timelineStart = timelineRows.reduce(
    (earliest, requisition) =>
      !earliest || (requisition.opened as string) < earliest ? (requisition.opened as string) : earliest,
    ''
  );
  const timelineEnd = timelineRows.reduce((latest, requisition) => {
    const end =
      requisition.status === 'COMPLETE' || requisition.status === 'CANCELLED'
        ? requisition.updated
        : asOf;
    return end && end > latest ? end : latest;
  }, asOf);
  const span = Math.max(1, daysBetween(timelineStart, timelineEnd) || 1);

  const position = (date: string | null) => {
    const offset = daysBetween(timelineStart, date);
    return offset === null ? 0 : Math.min(100, Math.max(0, (offset / span) * 100));
  };

  const requisitionColumns: HrColumn<HrRequisition>[] = [
    { key: 'req', header: 'Req', value: (row) => row.req },
    { key: 'role', header: 'Role', value: (row) => row.role || '—' },
    { key: 'dept', header: 'Department', value: (row) => row.dept || '—' },
    {
      key: 'status',
      header: 'Status',
      value: (row) => REQUISITION_STATUS_LABELS[row.status] || row.status,
      render: (row) => (
        <HrStatusPill tone={requisitionStatusTone(row.status)}>
          {REQUISITION_STATUS_LABELS[row.status] || row.status}
        </HrStatusPill>
      ),
    },
    {
      key: 'urgency',
      header: 'Urgency',
      value: (row) => row.urgency,
      render: (row) =>
        row.urgency === 'High' || row.urgency === 'Critical' ? (
          <HrStatusPill tone="danger">{row.urgency}</HrStatusPill>
        ) : (
          <HrStatusPill tone="muted">{row.urgency}</HrStatusPill>
        ),
    },
    {
      key: 'opened',
      header: 'Opened',
      value: (row) => row.opened || '',
      render: (row) => formatSydneyDateShort(row.opened) || '—',
    },
    {
      key: 'target',
      header: 'Target start',
      value: (row) => row.target || '',
      render: (row) => {
        const passed =
          row.target &&
          row.target < asOf &&
          (row.status === 'IN_PROGRESS' || row.status === 'OPEN');
        return passed ? (
          <HrStatusPill tone="danger">{formatSydneyDateShort(row.target)} passed</HrStatusPill>
        ) : (
          formatSydneyDateShort(row.target) || '—'
        );
      },
    },
    {
      key: 'cands',
      header: 'Candidates',
      align: 'right',
      value: (row) => row.cands,
      render: (row) => <span className="tabular-nums">{row.cands}</span>,
    },
    {
      key: 'salary',
      header: 'Salary range',
      align: 'right',
      value: (row) => salaryRange(row.smin, row.smax),
    },
  ];

  return (
    <div className="space-y-4">
      <HrKpiGrid
        items={[
          {
            label: 'Open requisitions',
            value: openRequisitions.length,
            detail: `${onHold.length} on hold`,
            icon: <Briefcase className="h-5 w-5" />,
          },
          {
            label: 'Candidates in pipeline',
            value: pipeline.length,
            detail: `${candidates.length} candidates on these requisitions`,
            icon: <Users className="h-5 w-5" />,
          },
          {
            label: 'Offers accepted / hired',
            value: accepted.length,
            icon: <Handshake className="h-5 w-5" />,
          },
          {
            label: 'Avg days to close',
            value: averageClose === null ? '—' : averageClose,
            detail: `${closed.length} completed requisitions`,
            icon: <Clock className="h-5 w-5" />,
          },
          {
            label: 'Offer acceptance',
            value: offerAcceptance,
            unit: '%',
            detail: 'Accepted ÷ (accepted + awaiting reply)',
            tone: offerAcceptance >= 60 ? 'success' : 'warning',
            icon: <UserPlus className="h-5 w-5" />,
          },
        ]}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Candidate funnel</CardTitle>
            <CardDescription>Current status of every candidate on these requisitions</CardDescription>
          </CardHeader>
          <CardContent>
            <HrBarList items={funnel} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Candidate source</CardTitle>
            <CardDescription>Where applicants came from</CardDescription>
          </CardHeader>
          <CardContent>
            <HrBarList items={sourceItems} />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Requisition timeline</CardTitle>
          <CardDescription>
            Bar = open period · marker = target start · {formatSydneyDateShort(timelineStart)} to{' '}
            {formatSydneyDateShort(timelineEnd)}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {timelineRows.length === 0 ? (
            <p className="py-6 text-sm text-muted-foreground">No requisitions found</p>
          ) : (
            <div className="max-h-[22rem] space-y-2 overflow-y-auto pr-1">
              {timelineRows.map((requisition) => {
                const start = position(requisition.opened);
                const end =
                  requisition.status === 'COMPLETE' || requisition.status === 'CANCELLED'
                    ? position(requisition.updated)
                    : 100;
                const target = position(requisition.target);

                return (
                  <div
                    key={requisition.id}
                    className="grid grid-cols-[minmax(120px,220px)_1fr] items-center gap-3"
                    title={`${requisition.req} · ${requisition.role || ''}\n${requisition.dept || ''} · ${
                      REQUISITION_STATUS_LABELS[requisition.status] || requisition.status
                    }\nOpened ${requisition.opened || '—'} · target ${requisition.target || '—'}\n${
                      requisition.cands
                    } candidates`}
                  >
                    <span className="truncate text-xs text-foreground">
                      {requisition.req} {requisition.role || ''}
                    </span>
                    <span className="relative h-3 rounded-full bg-muted">
                      <span
                        className="absolute inset-y-0 rounded-full"
                        style={{
                          left: `${start}%`,
                          width: `${Math.max(1, end - start)}%`,
                          backgroundColor:
                            TIMELINE_COLOURS[requisition.status] || 'var(--chart-3)',
                        }}
                      />
                      {requisition.target ? (
                        <span
                          className="absolute -top-1 h-5 w-0.5 bg-foreground"
                          style={{ left: `${target}%` }}
                        />
                      ) : null}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-4 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm bg-chart-2" /> In progress
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm bg-chart-3" /> Complete
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm bg-warning" /> On hold
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm bg-muted-foreground" /> Cancelled
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-3 w-0.5 bg-foreground" /> Target start
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Requisitions</CardTitle>
          <CardDescription>Status, urgency and volume</CardDescription>
        </CardHeader>
        <CardContent className="p-3">
          <HrDataTable
            rows={requisitions}
            columns={requisitionColumns}
            initialSort={{ key: 'opened', direction: 'desc' }}
            searchPlaceholder="Search requisitions, roles or departments…"
            emptyLabel="No requisitions found"
            viewName="Requisitions"
            filters={filters}
            asOf={asOf}
            canExport={canExport}
            onBeforeExport={checkExport}
            rowKey={(requisition) => requisition.id}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Candidates per requisition</CardTitle>
          <CardDescription>Applicant volume by role</CardDescription>
        </CardHeader>
        <CardContent>
          <HrBarList items={perRequisition} />
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        Total candidates on these requisitions: {formatNumber(candidates.length)}.
      </p>
    </div>
  );
}
